import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/api";
import { AlertTriangle, ArrowRight, Activity, ShieldCheck } from "lucide-react";

const STAGE_COLORS = {
  qualified: "#94A3B8", ready: "#38BDF8", sent: "#818CF8", replies: "#F472B6",
  conversations: "#C084FC", discovery: "#FBBF24", proposals: "#F97316",
  clients: "#34D399", revenue: "#10B981",
};

const SEVERITY_STYLES = {
  high: "border-rose-800/60 bg-rose-950/40 text-rose-300",
  medium: "border-amber-800/60 bg-amber-950/40 text-amber-300",
  low: "border-slate-700 bg-slate-900/60 text-slate-400",
};

function Funnel({ stages }) {
  const max = Math.max(...stages.filter((s) => !s.is_currency).map((s) => s.count), 1);
  return (
    <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="funnel-card">
      <h2 className="text-lg font-medium text-slate-200 tracking-tight">Pipeline Funnel</h2>
      <div className="space-y-1.5">
        {stages.map((s) => (
          <div key={s.stage} className="flex items-center gap-3" data-testid={`funnel-stage-${s.stage}`}>
            <span className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-wider text-slate-400">{s.label}</span>
            <div className="flex-1 h-5 bg-slate-900 rounded-sm overflow-hidden">
              <div
                className="h-full rounded-sm transition-all duration-500"
                style={{
                  width: s.is_currency ? "100%" : `${Math.max((s.count / max) * 100, s.count > 0 ? 6 : 0)}%`,
                  backgroundColor: STAGE_COLORS[s.stage],
                  opacity: s.is_currency ? 0.25 : 0.85,
                }}
              />
            </div>
            <span className="w-20 text-right font-mono text-sm" style={{ color: STAGE_COLORS[s.stage] }}>
              {s.is_currency ? fmtMoney(s.count) : s.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AttentionQueue({ items }) {
  return (
    <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="attention-queue">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-slate-200 tracking-tight">Decisions Requiring Attention</h2>
        <span className="font-mono text-xs text-amber-300" data-testid="attention-count">{items.length} pending</span>
      </div>
      {items.length === 0 && <p className="text-sm text-slate-500">Queue is clear.</p>}
      <div className="space-y-2">
        {items.map((i) => (
          <Link
            key={i.id}
            to={i.deep_link || "#"}
            data-testid={`attention-item-${i.id}`}
            className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border transition-colors duration-150 hover:border-slate-600 ${SEVERITY_STYLES[i.severity]}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{i.title}</div>
              <div className="text-xs opacity-75 truncate">{i.subtitle}</div>
            </div>
            <ArrowRight size={14} className="shrink-0 opacity-60" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function Exceptions({ items }) {
  return (
    <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="exceptions-card">
      <div className="flex items-center gap-2">
        <AlertTriangle size={15} className="text-rose-400" />
        <h2 className="text-lg font-medium text-slate-200 tracking-tight">Exceptions</h2>
      </div>
      {items.length === 0 && <p className="text-sm text-slate-500">No exceptions.</p>}
      <div className="space-y-1.5">
        {items.map((i) => (
          <div key={i.id} className="px-3 py-2 rounded-md border border-slate-800 bg-slate-950/50" data-testid={`exception-item-${i.id}`}>
            <div className="text-xs text-slate-300">{i.title}</div>
            <div className="font-mono text-[10px] text-slate-500">{i.subtitle}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Health({ health }) {
  return (
    <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="system-health-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={15} className="text-sky-400" />
          <h2 className="text-lg font-medium text-slate-200 tracking-tight">System Health</h2>
        </div>
        <span className="font-mono text-xs text-emerald-300" data-testid="telemetry-ok-rate">{health.telemetry_ok_rate}% ok</span>
      </div>
      <div className="space-y-1.5">
        {health.integrations.map((i) => (
          <div key={i.name} className="flex items-center justify-between text-xs" data-testid={`health-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="text-slate-300">{i.name}</span>
            <span className={`font-mono uppercase text-[10px] tracking-wider ${i.status === "mocked" ? "text-sky-400" : i.status === "manual" ? "text-amber-400" : "text-slate-500"}`}>
              {i.status.replace("_", " ")}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1 border-t border-slate-800/60">
        <ShieldCheck size={13} className="text-emerald-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400/90">
          ATLAS contract {health.atlas.contract_version} · {health.atlas.consumer === "none" ? "no consumer" : health.atlas.consumer}
        </span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading) return <div className="text-slate-500 font-mono text-sm" data-testid="dashboard-loading">Loading operating surface…</div>;
  const d = data || { funnel: [], attention: [], exceptions: [], health: { integrations: [], atlas: {} } };

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Pipeline, decisions and exceptions at a glance.</p>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">updated {new Date(d.updated_at).toLocaleTimeString()}</span>
      </div>
      <Funnel stages={d.funnel} />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2"><AttentionQueue items={d.attention} /></div>
        <div className="space-y-4">
          <Health health={d.health} />
          <Exceptions items={d.exceptions} />
        </div>
      </div>
    </div>
  );
}
